import { createFileRoute, Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  CopyButton,
  Divider,
  EmptyState,
  Group,
  Modal,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconChevronRight,
  IconKey,
  IconListDetails,
  IconReceipt2,
  IconSend,
  IconSettings,
  IconSignature,
  IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  StampingMode,
  useBumpSnapshot,
  useDeleteTenant,
  useRenderStatus,
  useRequestRender,
  useRotateStampingSecret,
  useSetStampingMode,
  useTenant,
  type TenantSummary,
} from '../api/tenants';
import { useTemplates } from '../api/templates';
import { senderScopeIsEmpty, useRules } from '../api/rules';
import { useDefaultTemplate } from '../api/users';
import { ApiError } from '../api/client';
import { PlatformRole, hasRole, useSession } from '../auth/session';
import { UsersScreen } from '../features/users/UsersScreen';
import { OutlookSidebarSettingsCard } from '../features/users/OutlookSidebarSettingsCard';
import { TenantDefaultFieldsCard } from '../features/directory/TenantDefaultFieldsCard';
import { CampaignsScreen } from '../features/campaigns/CampaignsScreen';
import { AccessScreen } from '../features/access/AccessScreen';
import { OnboardingBadge, StampingBadge } from '../tenants/status';
import { BackLink } from '../components/BackLink';
import { PageHeader } from '../components/PageHeader';
import { ErrorAlert } from '../components/ErrorAlert';
import { SettingsRow } from '../components/SettingsRow';
import { CardListSkeleton, DetailSkeleton } from '../components/Skeletons';
import { BillingPanel } from '../features/billing/BillingPanel';
import { GetHelpButton } from '../features/enquiries/GetHelpButton';

const tenantTabs = ['signatures', 'rules', 'campaigns', 'users', 'access', 'settings', 'billing'] as const;
type TenantTab = (typeof tenantTabs)[number];

function isTenantTab(value: string): value is TenantTab {
  return (tenantTabs as readonly string[]).includes(value);
}

/** Where Stripe drops the operator after a hosted checkout. Absent on every other visit. */
type CheckoutOutcome = 'success' | 'cancelled';

/** Flash from the Microsoft admin-consent callback. */
type DirectoryOutcome = 'connected' | 'denied' | 'error';

export const Route = createFileRoute('/tenants_/$tenantId')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab: TenantTab; billing?: CheckoutOutcome; directory?: DirectoryOutcome; reason?: string } => ({
    tab: typeof search.tab === 'string' && isTenantTab(search.tab) ? search.tab : 'settings',
    billing: search.billing === 'success' || search.billing === 'cancelled' ? search.billing : undefined,
    directory:
      search.directory === 'connected' || search.directory === 'denied' || search.directory === 'error'
        ? search.directory
        : undefined,
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
  component: TenantDetailPage,
});

function TenantDetailPage() {
  const { t } = useTranslation(['tenants', 'nav', 'common', 'billing', 'onboarding']);
  const { tenantId } = Route.useParams();
  const { tab, billing, directory, reason } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: session } = useSession();
  const { data: tenant, isPending, isError, error } = useTenant(tenantId);
  const queryClient = useQueryClient();

  // Stripe sends the operator back here with an outcome in the URL. Say what happened, re-read the
  // subscription, then strip the parameter: a reload or a bookmark should not replay the message,
  // and the redirect is a hint rather than evidence — the webhook is what makes it true.
  useEffect(() => {
    if (!billing) {
      return;
    }

    if (billing === 'success') {
      notifications.show({
        color: 'green',
        message: t('billing:checkoutCompleteToast'),
      });
      void queryClient.invalidateQueries({ queryKey: ['billing', tenantId] });
    } else {
      notifications.show({ color: 'gray', message: t('billing:checkoutCancelledToast') });
    }

    void navigate({ search: (prev) => ({ ...prev, billing: undefined }), replace: true });
  }, [billing, navigate, queryClient, t, tenantId]);

  // Microsoft admin consent returns here. The integration row was written by the callback; we only
  // tell the operator and refresh the Directory card.
  useEffect(() => {
    if (!directory) {
      return;
    }

    if (directory === 'connected') {
      notifications.show({
        color: 'green',
        message: t('onboarding:directoryConnected'),
      });
      void queryClient.invalidateQueries({ queryKey: ['integrations', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding', tenantId] });
    } else if (directory === 'denied') {
      notifications.show({
        color: 'orange',
        message: reason ?? t('onboarding:adminConsentDenied'),
      });
    } else {
      notifications.show({
        color: 'red',
        message: reason ?? t('onboarding:directoryConsentFailed'),
      });
    }

    void navigate({
      search: (prev) => ({ ...prev, directory: undefined, reason: undefined }),
      replace: true,
    });
  }, [directory, navigate, queryClient, reason, t, tenantId]);

  if (isPending) {
    return <DetailSkeleton />;
  }

  if (isError || !tenant) {
    const notFound = error instanceof ApiError && error.isNotFound;
    return (
      <Stack gap="md" maw={560} className="portal-enter">
        <BackLink to="/tenants">{t('tenants:title')}</BackLink>
        {notFound ? (
          <Alert color="red" title={t('tenants:tenantNotFound')}>
            {t('tenants:tenantNotFoundBody')}
          </Alert>
        ) : (
          <ErrorAlert title={t('tenants:loadTenantError')} error={error} />
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        back={<BackLink to="/tenants">{t('tenants:title')}</BackLink>}
        title={tenant.name}
        subtitle={
          <span style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}>{tenant.slug}</span>
        }
        meta={
          <>
            <OnboardingBadge state={tenant.onboardingState} />
            <StampingBadge tenant={tenant} />
          </>
        }
        actions={<GetHelpButton tenantId={tenant.id} tenantName={tenant.name} />}
        divider={false}
      />

      <Tabs
        value={tab}
        onChange={(value) => {
          if (value && isTenantTab(value)) {
            void navigate({ search: { tab: value } });
          }
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="signatures" leftSection={<IconSignature size={15} stroke={1.75} />}>
            {t('tenants:tabSignatures')}
          </Tabs.Tab>
          <Tabs.Tab value="rules" leftSection={<IconListDetails size={15} stroke={1.75} />}>
            {t('tenants:tabRules')}
          </Tabs.Tab>
          <Tabs.Tab value="campaigns" leftSection={<IconSend size={15} stroke={1.75} />}>
            {t('tenants:tabCampaigns')}
          </Tabs.Tab>
          <Tabs.Tab value="users" leftSection={<IconUsers size={15} stroke={1.75} />}>
            {t('tenants:tabUsers')}
          </Tabs.Tab>
          <Tabs.Tab value="access" leftSection={<IconKey size={15} stroke={1.75} />}>
            {t('tenants:tabAccess')}
          </Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={15} stroke={1.75} />}>
            {t('tenants:tabSettings')}
          </Tabs.Tab>
          <Tabs.Tab value="billing" leftSection={<IconReceipt2 size={15} stroke={1.75} />}>
            {t('tenants:tabBilling')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="signatures" pt="md">
          <SignaturesTab tenantId={tenant.id} />
        </Tabs.Panel>

        <Tabs.Panel value="rules" pt="md">
          <RulesTab tenantId={tenant.id} />
        </Tabs.Panel>

        <Tabs.Panel value="campaigns" pt="md">
          <CampaignsScreen
            tenantId={tenant.id}
            canEdit={hasRole(session, PlatformRole.Editor, tenant.id)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="users" pt="md">
          <UsersScreen
            tenantId={tenant.id}
            canAdmin={hasRole(session, PlatformRole.TenantAdmin, tenant.id)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="access" pt="md">
          <AccessScreen tenantId={tenant.id} />
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt="md">
          <SettingsTab tenant={tenant} canAdmin={hasRole(session, PlatformRole.TenantAdmin, tenant.id)} />
        </Tabs.Panel>

        <Tabs.Panel value="billing" pt="md">
          <BillingPanel tenantId={tenant.id} justCompletedCheckout={billing === 'success'} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function TemplateStatusBadge({ status }: { status: number }) {
  const { t } = useTranslation(['tenants', 'common']);
  const label =
    status === 0
      ? t('tenants:templateStatusDraft')
      : status === 1
        ? t('tenants:templateStatusPublished')
        : status === 2
          ? t('tenants:templateStatusArchived')
          : t('common:unknown');
  if (status === 1) {
    return (
      <Badge size="sm" color="green" variant="light">
        {label}
      </Badge>
    );
  }
  if (status === 2) {
    return (
      <Badge size="sm" color="gray" variant="outline">
        {label}
      </Badge>
    );
  }
  return (
    <Badge size="sm" color="gray" variant="light">
      {label}
    </Badge>
  );
}

/** The Signatures tab: the tenant's template library, each row opening straight in the designer. */
function SignaturesTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation('tenants');
  const { data: templates, isPending, isError, error } = useTemplates(tenantId);
  const { data: render } = useRenderStatus(tenantId);

  const openDesigner = (
    <Button
      renderRoot={(rootProps) => (
        <Link to="/tenants/$tenantId/designer" params={{ tenantId }} {...rootProps} />
      )}
      variant="light"
      size="compact-sm"
      leftSection={<IconSignature size={14} />}
    >
      {t('openDesigner')}
    </Button>
  );

  if (isPending) {
    return <CardListSkeleton count={2} height={64} />;
  }

  if (isError) {
    return <ErrorAlert title={t('loadTemplatesError')} error={error} />;
  }

  if (!templates || templates.length === 0) {
    return (
      <Card withBorder padding="xl">
        <EmptyState
          icon={<IconSignature size={28} stroke={1.5} />}
          withIndicatorBackground
          title={t('noTemplatesTitle')}
          description={t('noTemplatesDescription')}
        >
          <EmptyState.Actions>{openDesigner}</EmptyState.Actions>
        </EmptyState>
      </Card>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text size="sm" c="dimmed">
          {t('templatesAssignableHint')}
        </Text>
        {openDesigner}
      </Group>

      {render?.pending || render?.inFlight ? (
        <Alert color="blue" title={t('renderingSignaturesTitle')}>
          {t('renderingSignaturesBody')}
        </Alert>
      ) : null}

      {render?.lastError ? (
        <Alert color="red" title={t('lastRenderFailed')}>
          {render.lastError}
          {render.attempts > 1 ? ` ${t('retriedTimes', { count: render.attempts })}` : null}
        </Alert>
      ) : null}

      <Card withBorder padding={0}>
        {templates.map((template, index) => (
          <Fragment key={template.id}>
            {index > 0 && <Divider color="var(--portal-hairline)" />}
            <Link
              to="/tenants/$tenantId/designer"
              params={{ tenantId }}
              search={{ templateId: template.id }}
              style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
              className="portal-row-hover"
            >
              <Group px="md" py="sm" justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="light" color="iris" size={32} radius="sm">
                    <IconSignature size={16} stroke={1.75} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Group gap="xs">
                      <Text fw={500} size="sm">
                        {template.name}
                      </Text>
                      <TemplateStatusBadge status={template.status} />
                    </Group>
                    <Text size="xs" c="dimmed">
                      <span style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}>
                        v{template.version}
                      </span>
                      {template.publishedAt
                        ? ` · ${t('publishedDate', { date: new Date(template.publishedAt).toLocaleDateString() })}`
                        : ` · ${t('neverPublished')}`}
                    </Text>
                  </Stack>
                </Group>
                <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />
              </Group>
            </Link>
          </Fragment>
        ))}
      </Card>
    </Stack>
  );
}

/**
 * The Rules tab: a read-only summary of the evaluation order. Editing, reordering and testing
 * live in the rules editor screen, which owns the drag-and-drop machinery.
 *
 * The tenant default is included as a pinned catch-all row so an empty operator list does not read
 * as "nothing is stamped" when Users already has a default signature.
 */
function RulesTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation(['tenants', 'common']);
  const { data: rules, isPending, isError, error } = useRules(tenantId);
  const { data: templates } = useTemplates(tenantId);
  const { data: defaultTemplate } = useDefaultTemplate(tenantId);

  const openEditor = (
    <Button
      renderRoot={(rootProps) => (
        <Link to="/tenants/$tenantId/rules" params={{ tenantId }} {...rootProps} />
      )}
      variant="light"
      size="compact-sm"
      leftSection={<IconListDetails size={14} />}
    >
      {t('tenants:openRulesEditor')}
    </Button>
  );

  if (isPending) {
    return <CardListSkeleton count={3} height={56} />;
  }

  if (isError) {
    return <ErrorAlert title={t('tenants:loadRulesError')} error={error} />;
  }

  const templateName = (id: string | null) =>
    id === null ? null : (templates?.find((template) => template.id === id)?.name ?? null);

  const hasDefault = defaultTemplate?.templateNewId != null;
  const hasOperatorRules = (rules?.length ?? 0) > 0;

  if (!hasOperatorRules && !hasDefault) {
    return (
      <Card withBorder padding="xl">
        <EmptyState
          icon={<IconListDetails size={28} stroke={1.5} />}
          withIndicatorBackground
          title={t('tenants:noRulesTitle')}
          description={t('tenants:noRulesDescription')}
        >
          <EmptyState.Actions>{openEditor}</EmptyState.Actions>
        </EmptyState>
      </Card>
    );
  }

  const defaultNew = hasDefault ? templateName(defaultTemplate.templateNewId) : null;
  const defaultReply = hasDefault ? templateName(defaultTemplate.templateReplyId) : null;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text size="sm" c="dimmed">
          {t('tenants:rulesEvalHint')}
        </Text>
        {openEditor}
      </Group>
      <Card withBorder padding={0}>
        {hasOperatorRules &&
          rules.map((rule, index) => {
            const newTemplate = templateName(rule.templateNewId);
            const replyTemplate = templateName(rule.templateReplyId);
            return (
              <Fragment key={rule.id}>
                {index > 0 && <Divider color="var(--portal-hairline)" />}
                <Group px="md" py="sm" gap="sm" wrap="nowrap" align="flex-start">
                  <Center
                    w={24}
                    h={24}
                    fz={12}
                    fw={600}
                    c="dimmed"
                    style={{
                      borderRadius: '50%',
                      border: '1px solid var(--mantine-color-default-border)',
                      fontFamily: 'var(--mantine-font-family-monospace)',
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Center>
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap="xs">
                      <Text fw={500} size="sm" c={rule.enabled ? undefined : 'dimmed'}>
                        {rule.name}
                      </Text>
                      {!rule.enabled && (
                        <Badge size="xs" color="gray" variant="light">
                          {t('common:disabled')}
                        </Badge>
                      )}
                      {senderScopeIsEmpty(rule.senders) && (
                        <Tooltip label={t('tenants:matchesNobodyTooltip')}>
                          <Badge size="xs" color="yellow" variant="light">
                            {t('tenants:matchesNobody')}
                          </Badge>
                        </Tooltip>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {newTemplate
                        ? t('tenants:newWithTemplate', { name: newTemplate })
                        : t('tenants:newNoTemplate')}
                      {' · '}
                      {replyTemplate
                        ? t('tenants:replyWithTemplate', { name: replyTemplate })
                        : t('tenants:replySameAsNew')}
                    </Text>
                  </Stack>
                </Group>
              </Fragment>
            );
          })}
        {hasDefault && (
          <>
            {hasOperatorRules && <Divider color="var(--portal-hairline)" />}
            <Group px="md" py="sm" gap="sm" wrap="nowrap" align="flex-start">
              <Center
                w={24}
                h={24}
                fz={12}
                fw={600}
                c="dimmed"
                style={{
                  borderRadius: '50%',
                  border: '1px solid var(--mantine-color-default-border)',
                  fontFamily: 'var(--mantine-font-family-monospace)',
                  flexShrink: 0,
                }}
              >
                {(rules?.length ?? 0) + 1}
              </Center>
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Text fw={500} size="sm">
                    {t('tenants:primaryDefault')}
                  </Text>
                  <Badge size="xs" color="green" variant="light">
                    {t('tenants:defaultBadge')}
                  </Badge>
                  <Badge size="xs" variant="outline" color="gray">
                    {t('tenants:everyone')}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  {defaultNew
                    ? t('tenants:newWithTemplate', { name: defaultNew })
                    : t('tenants:newNoTemplate')}
                  {' · '}
                  {defaultReply
                    ? t('tenants:replyWithTemplate', { name: defaultReply })
                    : t('tenants:replySameAsNew')}
                </Text>
              </Stack>
            </Group>
          </>
        )}
      </Card>
    </Stack>
  );
}

function SettingsTab({ tenant, canAdmin }: { tenant: TenantSummary; canAdmin: boolean }) {
  const { t } = useTranslation(['tenants', 'common']);
  const { data: session } = useSession();
  const navigate = Route.useNavigate();
  const isMsp = hasRole(session, PlatformRole.MspAdmin);
  const isOwner = hasRole(session, PlatformRole.MspOwner);
  const [bypassOpen, setBypassOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [secretOpen, setSecretOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [issuedSecret, setIssuedSecret] = useState<string | null>(null);

  const bump = useBumpSnapshot(tenant.id);

  return (
    <Stack gap="md">
      {tenant.stampingMode === StampingMode.Bypass && (
        <Alert
          color={tenant.bypassedAt ? 'red' : 'orange'}
          title={
            tenant.bypassedAt
              ? t('tenants:stampingStoppedAutoTitle')
              : t('tenants:stampingBypassedTitle')
          }
        >
          {/*
            The first question anyone asks about a bypassed tenant is whether a colleague did it.
            bypassedAt answers it: a person switching stamping off leaves it null and their name in
            the audit log, so a timestamp here means nobody chose this and the fault is still live.
          */}
          {tenant.bypassedAt
            ? t('tenants:stampingStoppedAutoBody', {
                when: new Date(tenant.bypassedAt).toLocaleString(),
              })
            : t('tenants:stampingBypassedBody')}
          {tenant.bypassReasonNote ? ` ${tenant.bypassReasonNote}` : null}
        </Alert>
      )}

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('tenants:overview')}</Text>
          <SettingsRow label={t('tenants:slug')} value={tenant.slug} />
          <SettingsRow
            label={t('tenants:smartHost')}
            value={tenant.smartHost ?? t('common:none')}
            muted={!tenant.smartHost}
          />
          <SettingsRow
            label={t('tenants:verifiedDomains')}
            value={
              tenant.verifiedDomains.length === 0
                ? t('tenants:noneSynced')
                : tenant.verifiedDomains.join(', ')
            }
            muted={tenant.verifiedDomains.length === 0}
          />
          <SettingsRow label={t('tenants:snapshotVersion')} value={`v${tenant.snapshotVersion}`} />
        </Stack>
      </Card>

      <TenantDefaultFieldsCard tenantId={tenant.id} canAdmin={canAdmin} />

      <OutlookSidebarSettingsCard tenantId={tenant.id} canAdmin={canAdmin} />

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={4} maw={480}>
              <Text fw={500}>{t('tenants:stampingMode')}</Text>
              <Text size="sm" c="dimmed">
                {t('tenants:stampingModeHint')}
              </Text>
            </Stack>
            <StampingBadge tenant={tenant} />
          </Group>

          <Group>
            {tenant.stampingMode === StampingMode.Active ? (
              <Button
                color="orange"
                disabled={!canAdmin}
                onClick={() => setBypassOpen(true)}
              >
                {t('tenants:setBypass')}
              </Button>
            ) : (
              <Button disabled={!canAdmin} onClick={() => setResumeOpen(true)}>
                {t('tenants:resumeStamping')}
              </Button>
            )}
            {!canAdmin && (
              <Text size="xs" c="dimmed">
                {t('tenants:stampingModePermission')}
              </Text>
            )}
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>{t('tenants:operations')}</Text>
          <Text size="sm" c="dimmed">
            {t('tenants:operationsHint')}
          </Text>
          <Group>
            <Link to="/tenants/$tenantId/onboarding" params={{ tenantId: tenant.id }}>
              <Button variant="light">{t('tenants:onboardingWizard')}</Button>
            </Link>
            <Link to="/tenants/$tenantId/tester" params={{ tenantId: tenant.id }}>
              <Button variant="light">{t('tenants:configurationTester')}</Button>
            </Link>
            <OnboardingBadge state={tenant.onboardingState} />
          </Group>
        </Stack>
      </Card>

      <RenderCard tenantId={tenant.id} canAdmin={canAdmin} />

      {isMsp && (
        <Card withBorder padding="md">
          <Stack gap="sm">
            <Text fw={500}>{t('tenants:mspOperations')}</Text>
            <Text size="sm" c="dimmed">
              {t('tenants:mspOperationsHint')}
            </Text>
            <Group>
              <Button
                variant="default"
                loading={bump.isPending}
                onClick={() => {
                  bump.mutate(undefined, {
                    onSuccess: (result) => {
                      notifications.show({
                        color: 'green',
                        message: t('tenants:snapshotBumped', { version: result.snapshotVersion }),
                      });
                    },
                    onError: (err) => {
                      notifications.show({
                        color: 'red',
                        message: err instanceof Error ? err.message : t('tenants:bumpFailed'),
                      });
                    },
                  });
                }}
              >
                {t('tenants:bumpSnapshot')}
              </Button>
              <Button
                variant="light"
                color="red"
                onClick={() => {
                  setIssuedSecret(null);
                  setSecretOpen(true);
                }}
              >
                {t('tenants:rotateStampingSecret')}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {isOwner && (
        <Card withBorder padding="md" style={{ borderColor: 'var(--mantine-color-red-4)' }}>
          <Stack gap="sm">
            <Text fw={500}>{t('tenants:deleteTenant')}</Text>
            <Text size="sm" c="dimmed">
              {t('tenants:deleteTenantHint')}
            </Text>
            <Group>
              <Button color="red" onClick={() => setDeleteOpen(true)}>
                {t('tenants:deleteTenant')}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      <StampingModeModal
        opened={bypassOpen}
        onClose={() => setBypassOpen(false)}
        tenantId={tenant.id}
        mode={StampingMode.Bypass}
        title={t('tenants:setBypass')}
        confirmLabel={t('tenants:bypassConfirm')}
        confirmColor="orange"
        placeholder={t('tenants:bypassPlaceholder')}
      />

      <StampingModeModal
        opened={resumeOpen}
        onClose={() => setResumeOpen(false)}
        tenantId={tenant.id}
        mode={StampingMode.Active}
        title={t('tenants:resumeStamping')}
        confirmLabel={t('tenants:resumeConfirm')}
        confirmColor="blue"
        placeholder={t('tenants:resumePlaceholder')}
      />

      <RotateSecretModal
        opened={secretOpen}
        onClose={() => {
          setSecretOpen(false);
          setIssuedSecret(null);
        }}
        tenantId={tenant.id}
        issuedSecret={issuedSecret}
        onIssued={setIssuedSecret}
      />

      <DeleteTenantModal
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        tenant={tenant}
        onDeleted={() => {
          void navigate({ to: '/tenants' });
        }}
      />
    </Stack>
  );
}

/**
 * Where the tenant's rendered signatures stand, and the button that forces them to be rebuilt.
 *
 * Worth surfacing because rendering is the one step between publishing a design and mail actually
 * carrying it, and it happens out of sight on a worker. When something looks stale, this says
 * whether a pass is queued, running, or last failed and why — and re-render is the repair for the
 * case no automatic trigger covers, where artifacts drifted while the platform was down.
 */
function RenderCard({ tenantId, canAdmin }: { tenantId: string; canAdmin: boolean }) {
  const { t } = useTranslation('tenants');
  const { data: status } = useRenderStatus(tenantId);
  const request = useRequestRender(tenantId);

  const state = status?.inFlight
    ? t('renderingNow')
    : status?.pending
      ? t('queued')
      : status?.lastCompletedAt
        ? t('lastCompletedAt', { when: new Date(status.lastCompletedAt).toLocaleString() })
        : t('nothingRenderedYet');

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={4} maw={480}>
            <Text fw={500}>{t('signatureRendering')}</Text>
            <Text size="sm" c="dimmed">
              {t('signatureRenderingHint')}
            </Text>
          </Stack>
          <Badge variant="light" color={status?.lastError ? 'red' : status?.pending ? 'blue' : 'gray'}>
            {state}
          </Badge>
        </Group>

        {status?.lastError && (
          <Alert color="red" title={t('lastRenderFailed')}>
            {status.lastError}
          </Alert>
        )}

        <Group>
          <Button
            variant="default"
            disabled={!canAdmin}
            loading={request.isPending}
            onClick={() => {
              request.mutate(undefined, {
                onSuccess: () => {
                  notifications.show({
                    color: 'blue',
                    message: t('rerenderQueued'),
                  });
                },
                onError: (err) => {
                  notifications.show({
                    color: 'red',
                    message: err instanceof Error ? err.message : t('rerenderFailed'),
                  });
                },
              });
            }}
          >
            {t('rerenderSignatures')}
          </Button>
          {!canAdmin && (
            <Text size="xs" c="dimmed">
              {t('rerenderPermission')}
            </Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

function StampingModeModal({
  opened,
  onClose,
  tenantId,
  mode,
  title,
  confirmLabel,
  confirmColor,
  placeholder,
}: {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  mode: number;
  title: string;
  confirmLabel: string;
  confirmColor: string;
  placeholder: string;
}) {
  const { t } = useTranslation(['tenants', 'common']);
  const [note, setNote] = useState('');
  const setMode = useSetStampingMode(tenantId);

  const submit = () => {
    setMode.mutate(
      { mode, note: note.trim() },
      {
        onSuccess: () => {
          setNote('');
          onClose();
          notifications.show({
            color: mode === StampingMode.Bypass ? 'orange' : 'green',
            message:
              mode === StampingMode.Bypass
                ? t('tenants:stampingBypassedToast')
                : t('tenants:stampingResumedToast'),
          });
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      onExitTransitionEnd={() => {
        setNote('');
        setMode.reset();
      }}
    >
      <Stack>
        <Text size="sm" c="dimmed">
          {t('tenants:reasonRequiredHint')}
        </Text>
        <Textarea
          label={t('common:reason')}
          placeholder={placeholder}
          minRows={3}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          data-autofocus
        />
        {setMode.isError && (
          <Alert color="red" title={t('tenants:updateStampingError')}>
            {setMode.error instanceof Error ? setMode.error.message : t('common:unknownError')}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button
            color={confirmColor}
            loading={setMode.isPending}
            disabled={note.trim().length === 0}
            onClick={submit}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RotateSecretModal({
  opened,
  onClose,
  tenantId,
  issuedSecret,
  onIssued,
}: {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  issuedSecret: string | null;
  onIssued: (secret: string) => void;
}) {
  const { t } = useTranslation(['tenants', 'common', 'onboarding']);
  const [confirmText, setConfirmText] = useState('');
  const rotate = useRotateStampingSecret(tenantId);
  // Confirmation keyword stays English — operators type this exact token.
  const confirmWord = 'rotate';
  const confirmHint = t('tenants:rotateConfirmHint', { word: confirmWord });
  const confirmHintParts = confirmHint.split(confirmWord);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('tenants:rotateStampingSecret')}
      size="lg"
      onExitTransitionEnd={() => {
        setConfirmText('');
        rotate.reset();
      }}
    >
      <Stack>
        {issuedSecret ? (
          <>
            <Alert color="orange" title={t('tenants:copySecretNowTitle')}>
              {t('tenants:copySecretNowBody')}
            </Alert>
            <Code block>{issuedSecret}</Code>
            <CopyButton value={issuedSecret}>
              {({ copied, copy }) => (
                <Button variant={copied ? 'filled' : 'default'} color={copied ? 'teal' : undefined} onClick={copy}>
                  {copied ? t('common:copied') : t('tenants:copySecretButton')}
                </Button>
              )}
            </CopyButton>
            <Group justify="flex-end">
              <Button onClick={onClose}>{t('common:done')}</Button>
            </Group>
          </>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              {confirmHintParts.map((part, i) => (
                <Fragment key={i}>
                  {part}
                  {i < confirmHintParts.length - 1 ? <Badge size="sm">{confirmWord}</Badge> : null}
                </Fragment>
              ))}
            </Text>
            <TextInput
              label={t('tenants:confirmation')}
              value={confirmText}
              onChange={(e) => setConfirmText(e.currentTarget.value)}
              data-autofocus
            />
            {rotate.isError && (
              <Alert color="red" title={t('tenants:rotateSecretError')}>
                {rotate.error instanceof Error ? rotate.error.message : t('common:unknownError')}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                {t('common:cancel')}
              </Button>
              <Button
                color="red"
                loading={rotate.isPending}
                disabled={confirmText.trim().toLowerCase() !== confirmWord}
                onClick={() => {
                  rotate.mutate(undefined, {
                    onSuccess: (result) => onIssued(result.secret),
                  });
                }}
              >
                {t('onboarding:rotateSecret')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function DeleteTenantModal({
  opened,
  onClose,
  tenant,
  onDeleted,
}: {
  opened: boolean;
  onClose: () => void;
  tenant: TenantSummary;
  onDeleted: () => void;
}) {
  const { t } = useTranslation(['tenants', 'common']);
  const [confirmSlug, setConfirmSlug] = useState('');
  const remove = useDeleteTenant();
  const confirmed = confirmSlug.trim().toLowerCase() === tenant.slug.toLowerCase();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('tenants:deleteTenant')}
      onExitTransitionEnd={() => {
        setConfirmSlug('');
        remove.reset();
      }}
    >
      <Stack>
        <Alert color="red" title={t('tenants:deleteCannotUndo')}>
          <Text size="sm">{t('tenants:deleteWarningBody', { name: tenant.name, slug: tenant.slug })}</Text>
        </Alert>
        <TextInput
          label={t('tenants:typeSlugToConfirm', { slug: tenant.slug })}
          value={confirmSlug}
          onChange={(e) => setConfirmSlug(e.currentTarget.value)}
          data-autofocus
        />
        {remove.isError && (
          <Alert color="red" title={t('tenants:deleteTenantError')}>
            {remove.error instanceof Error ? remove.error.message : t('common:unknownError')}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button
            color="red"
            loading={remove.isPending}
            disabled={!confirmed}
            onClick={() => {
              remove.mutate(tenant.id, {
                onSuccess: () => {
                  onClose();
                  notifications.show({
                    color: 'green',
                    message: t('tenants:tenantDeleted', { name: tenant.name }),
                  });
                  onDeleted();
                },
              });
            }}
          >
            {t('tenants:deleteTenant')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
